import React from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Close,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const initialForm = {
  visit_name: "",
  care_plan: "",
  payment_service: "",
  carers_required: 1,
  repeat_every: 2,
  time_tolerance: 60,
  personalized_checks: [{ id: Date.now(), check_name: "" }],
  time_required_from: "",
  time_required_to: "",
  start_time: "",
  end_time: "",
  support_instructions: "",
  allow_photos: false,
  share_with_family: false,
  mandatory_notes: false,
  time_critical: false,
};

export const getTimeDiff = (start, end) => {
  const [fH, fM] = start.split(":").map(Number);
  const [tH, tM] = end.split(":").map(Number);
  let fromMin = fH * 60 + fM;
  let toMin = tH * 60 + tM;
  if (toMin < fromMin) toMin += 1440;
  return toMin - fromMin;
};

export const formatTotalMinutes = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
};

export const extractArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const SectionPaper = ({ children }) => (
  <Box
    sx={{
      borderRadius: "12px",
      overflow: "hidden",
      border: "1px solid #E2E8F0",
      bgcolor: "#FFFFFF",
    }}
  >
    {children}
  </Box>
);

export const SectionHeader = ({ icon: Icon, iconColor, bgColor, title, subtitle, chip }) => (
  <Box
    sx={{
      px: 2.5,
      py: 1.5,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      bgcolor: bgColor,
      borderBottom: "1px solid #E2E8F0",
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        bgcolor: alpha(iconColor, 0.08),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ color: iconColor, fontSize: 18 }} />
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="#5F7D95" sx={{ fontSize: "0.6rem" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    {chip}
  </Box>
);

export const ModalHeader = ({ onClose }) => {
  const theme = useTheme();
  return (
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: "#fff",
        fontWeight: 900,
        py: 2,
        pr: 1,
        letterSpacing: -0.3,
      }}
    >
      <Typography component="span" sx={{ fontSize: "1.1rem", fontWeight: 900, color: "#fff" }}>
        Manage Call of Visit
      </Typography>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{
          color: "#fff",
          bgcolor: alpha("#fff", 0.15),
          "&:hover": { bgcolor: alpha("#fff", 0.25) },
        }}
        aria-label="close"
      >
        <Close fontSize="small" />
      </IconButton>
    </DialogTitle>
  );
};

export const ModalFooter = ({ onClose, isSubmitting }) => (
  <DialogActions
    sx={{
      px: 3,
      py: 2,
      bgcolor: "#FFFFFF",
      borderTop: "1px solid #E2E8F0",
    }}
  >
    <Button
      variant="outlined"
      onClick={onClose}
      disabled={isSubmitting}
      startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
      size="small"
      sx={{
        textTransform: "none",
        fontWeight: 500,
        borderRadius: "6px",
        borderColor: "#D0D5DD",
        color: "#344054",
        px: 3,
        py: 0.75,
        "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
      }}
    >
      Cancel
    </Button>
    <Button
      type="submit"
      variant="contained"
      disabled={isSubmitting}
      startIcon={
        isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 18 }} />
      }
      size="small"
      sx={{
        textTransform: "none",
        fontWeight: 600,
        borderRadius: "6px",
        bgcolor: "#2469F0",
        px: 3,
        py: 0.75,
        "&:hover": {
          bgcolor: "#1A5BD4",
          boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
        },
      }}
    >
      {isSubmitting ? "Creating..." : "Create Visit"}
    </Button>
  </DialogActions>
);